import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getLatestMessageTimestampForPeer } from "../lib/db";
import { subscribeTabSync } from "../lib/tabSync";
import { BootScreen } from "../components/BootScreen";
import { ConnectionOverlay } from "../components/ConnectionOverlay";
import { ChatView } from "../components/ChatView";
import { VoiceCallOverlay } from "../components/VoiceCallOverlay";
import { ContactList } from "../components/ContactList";
import { WorkspaceNavSection } from "../components/WorkspaceNavSection";
import { WorkspaceTopicRail } from "../components/WorkspaceTopicRail";
import { WorkspaceTopicsScreen } from "../components/WorkspaceTopicsScreen";
import type { WorkspaceTopicSelectArgs } from "../components/WorkspaceTopicList";
import type { WorkspaceSearchSelectArgs } from "../components/WorkspaceMessageSearchPanel";
import { Header } from "../components/Header";
import { SettingsPanel } from "../components/SettingsPanel";
import { ForwardMessageModal } from "../components/ForwardMessageModal";
import { MatrixRain } from "../components/MatrixRain";
import { Scanlines } from "../components/Scanlines";
import { useNarrowScreen } from "../hooks/useNarrowScreen";
import {
  narrowChatBackAction,
  useNarrowChatHistory,
  type NarrowHistoryHandlers,
  type NarrowOpenChat,
} from "../hooks/useNarrowChatHistory";
import { usePushBanner } from "../hooks/usePushBanner";
import { useReconnectOverlay } from "../hooks/useReconnectOverlay";
import { useTypingPresence } from "../hooks/useTypingPresence";
import { isDisplayableImageFile } from "../lib/displayableImageMime";
import {
  isChatFileAttachment,
  isChatImageAttachment,
  isChatVoiceAttachment,
  parseMessageEnvelope,
  plaintextDisplayBody,
  previewTextForReply,
} from "../lib/crypto";
import { apiFetchWorkspaceTopicMessages, apiMarkWorkspaceTopicRead, apiPinWorkspaceTopic, apiUnpinWorkspaceTopic, getStoredJwt } from "../lib/authApi";
import { notifyWorkspaceTopicListStale, registerWorkspaceTopicUiSink } from "../lib/workspaceTopicListSync";
import { rememberWorkspaceTopic } from "../lib/workspaceTopicMemory";
import {
  serverWorkspaceMessageToRow,
  workspaceReactionsMapFromServerMessages,
} from "../lib/workspaceTopicMessages";
import { STYX_THEME } from "../styles/theme";
import { SessionResetProvider } from "./SessionResetContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ContactsProvider, useContacts } from "./context/ContactsContext";
import { ChatProvider, useChat } from "./context/ChatContext";
import { CallProvider, useCall } from "./context/CallContext";
import { RealtimeProvider, useRealtime } from "./context/RealtimeContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { StyxInfrastructureBinder } from "./StyxInfrastructureBinder";
import { ThemeAccountSync } from "./ThemeAccountSync";
import { newFrameId } from "../lib/frame";
import { normalizeUsername, USERNAME_MAX_LEN } from "./state/appUtils";
import { VaultSection } from "../components/VaultSection";
import { dmPeerIdFromConversation, vaultIdFromConversation, type ConversationRef } from "./state/chatState";
import { VaultProvider, useVault } from "./context/VaultContext";
import { PinsProvider, usePins } from "./context/PinsContext";
import { apiFetchVaultBlob } from "../lib/authApi";
import { CHAT_PAGE_SIZE } from "../lib/db";
import type { MessageRow } from "../lib/db";

function StyxAppProviders({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <SessionResetProvider>
        <AuthProvider>
          <ThemeAccountSync />
          <ContactsProvider>
            <RealtimeProvider>
              <PinsProvider>
              <VaultProvider>
              <ChatProvider>
                <CallProvider>
                  <StyxInfrastructureBinder />
                  {children}
                </CallProvider>
              </ChatProvider>
              </VaultProvider>
              </PinsProvider>
            </RealtimeProvider>
          </ContactsProvider>
        </AuthProvider>
      </SessionResetProvider>
    </ThemeProvider>
  );
}

function forwardPreviewFromPlaintext(plaintext: string): string {
  const env = parseMessageEnvelope(plaintext);
  if (!env || env.content_type === "text" || env.content_type === "reaction" || env.content_type === "deleted") {
    return plaintextDisplayBody(plaintext) || "Текст";
  }
  if (env.content_type === "image") {
    const imageAtt = env.attachments[0];
    const caption = env.body?.trim();
    if (caption) return caption;
    return isChatImageAttachment(imageAtt) ? "[Изображение]" : "[Вложение]";
  }
  if (env.content_type === "file") {
    const fileAtt = env.attachments[0];
    const caption = env.body?.trim();
    if (caption) return caption;
    if (isChatFileAttachment(fileAtt)) {
      return isDisplayableImageFile(fileAtt.mime) ? "[Изображение]" : `[Файл: ${fileAtt.filename}]`;
    }
    return "[Файл]";
  }
  if (env.content_type === "voice") {
    const voiceAtt = env.attachments[0];
    return isChatVoiceAttachment(voiceAtt) ? "[Голосовое]" : "[Аудио]";
  }
  return plaintextDisplayBody(plaintext) || "Сообщение";
}

function StyxShellMain() {
  const { visualMode } = useTheme();
  const {
    sodiumReady,
    identityRow,
    keys,
    authTab,
    setAuthTab,
    regEmail,
    setRegEmail,
    regPassword,
    setRegPassword,
    regPassword2,
    setRegPassword2,
    regUsername,
    setRegUsername,
    regInviteCode,
    setRegInviteCode,
    loginEmail,
    setLoginEmail,
    loginPassword,
    setLoginPassword,
    bootError,
    bootItems,
    bootVisible,
    submitRegister,
    submitLogin,
    logoutLocal,
    handleRenameUsername,
    renameUsernameError,
    setRenameUsernameError,
    myAvatarUrl,
    myAvatarUrls,
    setMyAvatarUrls,
    registerSuccessTick,
    pwaBootUpdate,
    bootOverlayMode,
  } = useAuth();

  const { contactsState, dispatchContacts } = useContacts();
  const { conn, authenticated, isWsLeader, sendRealtimeCommand } = useRealtime();
  const {
    chatState,
    conversationCapabilities,
    dispatchChat,
    conversationLoading,
    loadingOlder,
    loadOlderChatMessages,
    forwardMessageToConversation,
    sendChat,
    toggleReaction,
    pickChatImage,
    pickChatFile,
    removeChatImage,
    sendVoiceMessage,
  } = useChat();
  const { voiceUi, setVoiceUi, voiceSessionRef, remoteAudioRef, startVoiceCall } = useCall();
  const { vaults, touchActivity, unlocked: vaultUnlocked } = useVault();
  const { isPinned, getPinnedAt } = usePins();

  const selectedDmPeerId = useMemo(
    () => dmPeerIdFromConversation(chatState.selectedConversation),
    [chatState.selectedConversation],
  );

  const selectedVaultId = useMemo(
    () => vaultIdFromConversation(chatState.selectedConversation),
    [chatState.selectedConversation],
  );

  const vaultTopicForView = useMemo(() => {
    const id = selectedVaultId;
    if (!id) return undefined;
    const name = vaults.find((v) => v.id === id)?.name ?? "Vault";
    return { vaultId: id, vaultName: name };
  }, [selectedVaultId, vaults]);

  const fetchVaultBlob = useCallback((blobId: string) => apiFetchVaultBlob(blobId), []);

  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Подписи для шапки чата workspace topic (имена из сайдбара или из history.state). */
  const [workspaceTopicLabels, setWorkspaceTopicLabels] = useState<{
    workspaceName: string;
    topicName: string;
  } | null>(null);
  const [workspaceSearchJump, setWorkspaceSearchJump] = useState<{
    workspaceId: string;
    workspaceName: string;
    topicId: string;
    topicName: string;
    messageId: string;
    createdAt: number;
  } | null>(null);
  const [pinnedTopicIdsByWorkspace, setPinnedTopicIdsByWorkspace] = useState<Record<string, string[]>>({});
  const [forwardDraft, setForwardDraft] = useState<{
    message: MessageRow;
    sourceConversation: ConversationRef;
    previewText: string;
  } | null>(null);

  const [sidebarWorkspace, setSidebarWorkspace] = useState<{ id: string; name: string; role: string } | null>(null);
  const [mobileWorkspaceTopicsView, setMobileWorkspaceTopicsView] = useState<{
    id: string;
    name: string;
    role: string;
  } | null>(null);
  const canCreateTopics = sidebarWorkspace?.role === "owner" || sidebarWorkspace?.role === "admin";

  const [topicListEpoch, setTopicListEpoch] = useState<Record<string, number>>({});
  /** Refetch GET /api/workspaces (sidebar unread totals) after workspace topic activity. */
  const [workspaceNavListEpoch, setWorkspaceNavListEpoch] = useState(0);
  /** Mobile sidebar: collapsible section toggles */
  const [mobileSidebarWsOpen, setMobileSidebarWsOpen] = useState(true);
  const [mobileSidebarDmOpen, setMobileSidebarDmOpen] = useState(true);
  const [mobileSidebarVaultsOpen, setMobileSidebarVaultsOpen] = useState(false);
  const [desktopSidebarVaultsOpen, setDesktopSidebarVaultsOpen] = useState(true);
  /** Last DM message time per peer (IndexedDB) — refresh on contact list / message changes */
  const [lastDmMessageAtByPeer, setLastDmMessageAtByPeer] = useState<Record<string, number>>({});
  const [dmContactSortEpoch, setDmContactSortEpoch] = useState(0);
  const workspaceSearchJumpFetchKeyRef = useRef<string | null>(null);
  const mobileSidebarScrollRootRef = useRef<HTMLDivElement>(null);
  const savedMobileSidebarScrollTopRef = useRef(0);

  useEffect(() => {
    const listDebounceMs = 320;
    const readDebounceMs = 400;
    const listTimers: Record<string, ReturnType<typeof setTimeout>> = {};
    const readTimers: Record<string, ReturnType<typeof setTimeout>> = {};
    let workspaceNavTimer: ReturnType<typeof setTimeout> | null = null;
    registerWorkspaceTopicUiSink({
      notifyListRefresh: (workspaceId) => {
        const prev = listTimers[workspaceId];
        if (prev) clearTimeout(prev);
        listTimers[workspaceId] = setTimeout(() => {
          delete listTimers[workspaceId];
          setTopicListEpoch((e) => ({ ...e, [workspaceId]: (e[workspaceId] ?? 0) + 1 }));
        }, listDebounceMs);
        if (workspaceNavTimer) clearTimeout(workspaceNavTimer);
        workspaceNavTimer = setTimeout(() => {
          workspaceNavTimer = null;
          setWorkspaceNavListEpoch((n) => n + 1);
        }, listDebounceMs);
      },
      scheduleMarkTopicRead: (workspaceId, topicId) => {
        const key = `${workspaceId}:${topicId}`;
        const prev = readTimers[key];
        if (prev) clearTimeout(prev);
        readTimers[key] = setTimeout(() => {
          delete readTimers[key];
          void apiMarkWorkspaceTopicRead(workspaceId, topicId)
            .then(() => {
              notifyWorkspaceTopicListStale(workspaceId);
            })
            .catch((e: unknown) => {
              console.warn("[styx] mark-read failed:", e);
            });
        }, readDebounceMs);
      },
    });
    return () => {
      registerWorkspaceTopicUiSink(null);
      for (const t of Object.values(listTimers)) clearTimeout(t);
      for (const t of Object.values(readTimers)) clearTimeout(t);
      if (workspaceNavTimer) clearTimeout(workspaceNavTimer);
    };
  }, []);

  const selectedWorkspaceTopicEpoch =
    chatState.selectedConversation?.kind === "workspace-topic"
      ? (topicListEpoch[chatState.selectedConversation.workspaceId] ?? 0)
      : 0;

  const prevConversationRef = useRef<ConversationRef | null>(chatState.selectedConversation);
  useEffect(() => {
    const prev = prevConversationRef.current;
    const next = chatState.selectedConversation;
    prevConversationRef.current = next;
    if (prev?.kind === "workspace-topic" && next?.kind !== "workspace-topic") {
      setWorkspaceTopicLabels(null);
    }
  }, [chatState.selectedConversation]);

  const narrowOpenChat = useMemo<NarrowOpenChat>(() => {
    if (mobileWorkspaceTopicsView) {
      return {
        kind: "workspace-topics",
        workspaceId: mobileWorkspaceTopicsView.id,
        workspaceName: mobileWorkspaceTopicsView.name,
      };
    }
    const c = chatState.selectedConversation;
    if (!c) return null;
    if (c.kind === "dm") return { kind: "dm", peerId: c.peerId };
    if (c.kind === "vault") {
      const name = vaults.find((v) => v.id === c.vaultId)?.name;
      return { kind: "vault", vaultId: c.vaultId, vaultName: name };
    }
    return {
      kind: "workspace-topic",
      workspaceId: c.workspaceId,
      topicId: c.topicId,
      workspaceName: workspaceTopicLabels?.workspaceName,
      topicName: workspaceTopicLabels?.topicName,
    };
  }, [chatState.selectedConversation, mobileWorkspaceTopicsView, workspaceTopicLabels, vaults]);

  const selectedTopicKey =
    chatState.selectedConversation?.kind === "workspace-topic"
      ? {
          workspaceId: chatState.selectedConversation.workspaceId,
          topicId: chatState.selectedConversation.topicId,
        }
      : null;

  const workspaceTopicForView = useMemo(() => {
    const c = chatState.selectedConversation;
    if (c?.kind !== "workspace-topic") return undefined;
    const base = {
      workspaceId: c.workspaceId,
      topicId: c.topicId,
      canCreateTopics,
      selectedTopicKey,
      topicsRefreshEpoch: selectedWorkspaceTopicEpoch,
    };
    const workspaceRole =
      sidebarWorkspace?.id === c.workspaceId ? sidebarWorkspace.role : undefined;
    return workspaceTopicLabels
      ? {
          ...base,
          workspaceName: workspaceTopicLabels.workspaceName,
          topicName: workspaceTopicLabels.topicName,
          workspaceRole,
        }
      : {
          ...base,
          workspaceName: "// workspace",
          topicName: "// topic",
          workspaceRole,
        };
  }, [
    chatState.selectedConversation,
    canCreateTopics,
    selectedTopicKey,
    workspaceTopicLabels,
    selectedWorkspaceTopicEpoch,
    sidebarWorkspace?.id,
    sidebarWorkspace?.role,
  ]);

  const onPinnedTopicIdsChange = useCallback((ws: string, ids: string[]) => {
    setPinnedTopicIdsByWorkspace((prev) => ({ ...prev, [ws]: ids }));
  }, []);

  const handleToggleSharedTopicPin = useCallback(async () => {
    const c = chatState.selectedConversation;
    if (c?.kind !== "workspace-topic") return;
    const { workspaceId, topicId } = c;
    const pinned = pinnedTopicIdsByWorkspace[workspaceId]?.includes(topicId) ?? false;
    try {
      if (pinned) {
        await apiUnpinWorkspaceTopic(workspaceId, topicId);
        setPinnedTopicIdsByWorkspace((p) => ({
          ...p,
          [workspaceId]: (p[workspaceId] ?? []).filter((id) => id !== topicId),
        }));
      } else {
        await apiPinWorkspaceTopic(workspaceId, topicId);
        setPinnedTopicIdsByWorkspace((p) => ({
          ...p,
          [workspaceId]: [...(p[workspaceId] ?? []).filter((id) => id !== topicId), topicId],
        }));
      }
    } catch {
      /* toast via chat error if needed */
    }
  }, [chatState.selectedConversation, pinnedTopicIdsByWorkspace]);

  useEffect(() => {
    const c = chatState.selectedConversation;
    if (c?.kind !== "workspace-topic") return;
    rememberWorkspaceTopic(c.workspaceId, c.topicId);
    // Only refresh name for the currently active sidebar workspace.
    // Never switch sidebarWorkspace to a different workspace from here —
    // workspace switching is done exclusively through onSelectWorkspaceRow
    // and narrowHistoryHandlers which set sidebarWorkspace explicitly.
    // Switching from here caused a stale-selectedConversation race: when the
    // user clicked workspace A, setWorkspaceTopicLabels(null) triggered this
    // effect with the old selectedConversation (workspace B), overwriting
    // sidebarWorkspace back to B even though the user had already chosen A.
    setSidebarWorkspace((prev) => {
      if (!prev || prev.id !== c.workspaceId) return prev;
      const nm = workspaceTopicLabels?.workspaceName;
      if (nm && nm !== prev.name) return { ...prev, name: nm };
      return prev;
    });
  }, [chatState.selectedConversation, workspaceTopicLabels?.workspaceName]);

  useEffect(() => {
    if (registerSuccessTick > 0) {
      setSettingsOpen(true);
    }
  }, [registerSuccessTick]);

  useEffect(() => {
    if (!identityRow || !keys) {
      setSettingsOpen(false);
    }
  }, [identityRow, keys]);

  const narrow = useNarrowScreen();
  const shellMain = Boolean(identityRow && keys && getStoredJwt());

  useEffect(() => {
    return subscribeTabSync((msg) => {
      if (msg.type === "messages-changed" || (msg.type === "idb-changed" && msg.scope === "messages")) {
        setDmContactSortEpoch((n) => n + 1);
      }
    });
  }, []);

  useEffect(() => {
    if (!shellMain || contactsState.contacts.length === 0) {
      setLastDmMessageAtByPeer({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const next: Record<string, number> = {};
      for (const c of contactsState.contacts) {
        const t = await getLatestMessageTimestampForPeer(c.user_id);
        if (t != null) next[c.user_id] = t;
      }
      if (!cancelled) setLastDmMessageAtByPeer(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [shellMain, contactsState.contacts, dmContactSortEpoch]);

  const contactsSortedByLastMessage = useMemo(() => {
    const list = [...contactsState.contacts];
    list.sort((a, b) => {
      const ap = isPinned("contact", a.user_id) ? 1 : 0;
      const bp = isPinned("contact", b.user_id) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      if (ap && bp) return (getPinnedAt("contact", a.user_id) ?? 0) - (getPinnedAt("contact", b.user_id) ?? 0);
      const ta = lastDmMessageAtByPeer[a.user_id] ?? 0;
      const tb = lastDmMessageAtByPeer[b.user_id] ?? 0;
      if (tb !== ta) return tb - ta;
      return a.username.localeCompare(b.username);
    });
    return list;
  }, [contactsState.contacts, lastDmMessageAtByPeer, isPinned, getPinnedAt]);

  useEffect(() => {
    if (!shellMain) setSidebarWorkspace(null);
    if (!shellMain) setMobileWorkspaceTopicsView(null);
  }, [shellMain]);

  const prevVaultUnlockedRef = useRef(vaultUnlocked);
  useEffect(() => {
    const was = prevVaultUnlockedRef.current;
    prevVaultUnlockedRef.current = vaultUnlocked;
    if (was && !vaultUnlocked && chatState.selectedConversation?.kind === "vault") {
      dispatchChat({ type: "set-selected-conversation", conversation: null });
    }
  }, [vaultUnlocked, chatState.selectedConversation, dispatchChat]);

  const narrowHistoryHandlers = useMemo<NarrowHistoryHandlers>(
    () => ({
      onListRoot: () => {
        setSettingsOpen(false);
        setMobileWorkspaceTopicsView(null);
        setWorkspaceTopicLabels(null);
        dispatchChat({ type: "set-selected-conversation", conversation: null });
      },
      onChat: (peerId: string) => {
        setSettingsOpen(false);
        setMobileWorkspaceTopicsView(null);
        setWorkspaceTopicLabels(null);
        setSidebarWorkspace(null);
        dispatchChat({ type: "set-selected-conversation", conversation: { kind: "dm", peerId } });
      },
      onVault: (vaultId: string) => {
        setSettingsOpen(false);
        setMobileWorkspaceTopicsView(null);
        setWorkspaceTopicLabels(null);
        setSidebarWorkspace(null);
        dispatchChat({ type: "set-selected-conversation", conversation: { kind: "vault", vaultId } });
      },
      onWorkspaceTopics: (workspaceId, labels) => {
        setSettingsOpen(false);
        setWorkspaceTopicLabels(null);
        setMobileWorkspaceTopicsView((prev) => ({
          id: workspaceId,
          name: labels?.workspaceName ?? prev?.name ?? "// workspace",
          role: prev?.id === workspaceId ? prev.role : sidebarWorkspace?.id === workspaceId ? sidebarWorkspace.role : "member",
        }));
        setSidebarWorkspace((prev) => ({
          id: workspaceId,
          name:
            labels?.workspaceName ??
            prev?.name ??
            (sidebarWorkspace?.id === workspaceId ? sidebarWorkspace.name : "// workspace"),
          role:
            prev?.id === workspaceId
              ? prev.role
              : sidebarWorkspace?.id === workspaceId
                ? sidebarWorkspace.role
                : "member",
        }));
        dispatchChat({ type: "set-selected-conversation", conversation: null });
      },
      onWorkspaceTopic: (workspaceId, topicId, labels) => {
        setSettingsOpen(false);
        setMobileWorkspaceTopicsView(null);
        const L = labels ?? {
          workspaceName: "// workspace",
          topicName: "// topic",
        };
        setWorkspaceTopicLabels(L);
        setSidebarWorkspace((prev) => ({
          id: workspaceId,
          name: L.workspaceName,
          role: prev?.id === workspaceId ? (prev.role ?? "member") : "member",
        }));
        dispatchChat({
          type: "set-selected-conversation",
          conversation: { kind: "workspace-topic", workspaceId, topicId },
        });
      },
      onSettings: () => {
        setMobileWorkspaceTopicsView(null);
        setWorkspaceTopicLabels(null);
        dispatchChat({ type: "set-selected-conversation", conversation: null });
        setSettingsOpen(true);
      },
    }),
    [dispatchChat, sidebarWorkspace],
  );
  useNarrowChatHistory(shellMain, narrow, narrowOpenChat, settingsOpen, narrowHistoryHandlers);

  const { showPushBanner, pushBannerError, onEnablePushTap, onDismissPushBanner } = usePushBanner({
    authenticated,
    hasIdentity: Boolean(identityRow),
    identityKey: identityRow?.userId ?? null,
  });

  const sendContactRequest = useCallback(() => {
    const username = normalizeUsername(contactsState.addUser);
    if (!username) return;
    sendRealtimeCommand("contacts.request", { username }, newFrameId());
    dispatchContacts({ type: "set-add-user", value: "" });
  }, [contactsState.addUser, dispatchContacts, sendRealtimeCommand]);

  const resolveTopicAuthorName = useCallback(
    (authorId: string) => {
      const row = contactsState.contacts.find((contact) => contact.user_id === authorId);
      return row?.username ?? null;
    },
    [contactsState.contacts],
  );

  const acceptRequest = useCallback(
    (fromId: string) => {
      sendRealtimeCommand("contacts.accept", { from_id: fromId }, newFrameId());
    },
    [sendRealtimeCommand],
  );

  const onSelectWorkspaceTopic = useCallback(
    (args: WorkspaceTopicSelectArgs) => {
      setSettingsOpen(false);
      setMobileWorkspaceTopicsView(null);
      setWorkspaceSearchJump(null);
      setWorkspaceTopicLabels({ workspaceName: args.workspaceName, topicName: args.topicName });
      dispatchChat({
        type: "set-selected-conversation",
        conversation: { kind: "workspace-topic", workspaceId: args.workspaceId, topicId: args.topicId },
      });
    },
    [dispatchChat],
  );

  const onSelectWorkspaceSearchResult = useCallback(
    (args: WorkspaceSearchSelectArgs) => {
      const workspaceId =
        mobileWorkspaceTopicsView?.id ??
        (chatState.selectedConversation?.kind === "workspace-topic"
          ? chatState.selectedConversation.workspaceId
          : sidebarWorkspace?.id ?? null);
      const workspaceName =
        mobileWorkspaceTopicsView?.name ??
        workspaceTopicLabels?.workspaceName ??
        sidebarWorkspace?.name ??
        "// workspace";
      if (!workspaceId) return;
      setSettingsOpen(false);
      setMobileWorkspaceTopicsView(null);
      setWorkspaceTopicLabels({ workspaceName, topicName: args.topicName });
      setWorkspaceSearchJump({
        workspaceId,
        workspaceName,
        topicId: args.topicId,
        topicName: args.topicName,
        messageId: args.messageId,
        createdAt: args.createdAt,
      });
      dispatchChat({
        type: "set-selected-conversation",
        conversation: { kind: "workspace-topic", workspaceId, topicId: args.topicId },
      });
    },
    [
      chatState.selectedConversation,
      dispatchChat,
      mobileWorkspaceTopicsView,
      sidebarWorkspace?.id,
      sidebarWorkspace?.name,
      workspaceTopicLabels?.workspaceName,
    ],
  );

  const onWorkspaceRenamedFromTopics = useCallback((wsId: string, newName: string) => {
    setMobileWorkspaceTopicsView((prev) => (prev && prev.id === wsId ? { ...prev, name: newName } : prev));
    setSidebarWorkspace((prev) => (prev && prev.id === wsId ? { ...prev, name: newName } : prev));
    setWorkspaceNavListEpoch((n) => n + 1);
  }, []);

  const onWorkspaceTopicRenamed = useCallback(
    (topicName: string) => {
      setWorkspaceTopicLabels((prev) => (prev ? { ...prev, topicName } : prev));
      const c = chatState.selectedConversation;
      if (c?.kind === "workspace-topic") {
        setTopicListEpoch((e) => ({
          ...e,
          [c.workspaceId]: (e[c.workspaceId] ?? 0) + 1,
        }));
      }
    },
    [chatState.selectedConversation],
  );

  const onSelectWorkspaceRow = useCallback(
    (w: { id: string; name: string; role?: string }) => {
      setSettingsOpen(false);
      setWorkspaceSearchJump(null);
      setWorkspaceTopicLabels(null);
      setSidebarWorkspace({ id: w.id, name: w.name, role: w.role ?? "member" });
      if (narrow) {
        setMobileWorkspaceTopicsView({ id: w.id, name: w.name, role: w.role ?? "member" });
        dispatchChat({ type: "set-selected-conversation", conversation: null });
      }
    },
    [dispatchChat, narrow],
  );

  useEffect(() => {
    if (!workspaceSearchJump) {
      workspaceSearchJumpFetchKeyRef.current = null;
      return;
    }
    const current = chatState.selectedConversation;
    if (
      current?.kind !== "workspace-topic" ||
      current.workspaceId !== workspaceSearchJump.workspaceId ||
      current.topicId !== workspaceSearchJump.topicId
    ) {
      return;
    }
    if (chatState.messages.some((message) => message.id === workspaceSearchJump.messageId)) {
      workspaceSearchJumpFetchKeyRef.current = null;
      return;
    }
    if (chatState.messages.length === 0 || !chatState.chatHasMoreOlder) {
      return;
    }
    const fetchKey = `${workspaceSearchJump.workspaceId}:${workspaceSearchJump.topicId}:${workspaceSearchJump.messageId}`;
    if (workspaceSearchJumpFetchKeyRef.current === fetchKey) return;
    workspaceSearchJumpFetchKeyRef.current = fetchKey;
    let cancelled = false;

    void (async () => {
      let oldest = chatState.messages[0]!;
      let hasMore = chatState.chatHasMoreOlder;
      while (!cancelled && hasMore) {
        const { messages } = await apiFetchWorkspaceTopicMessages(
          workspaceSearchJump.workspaceId,
          workspaceSearchJump.topicId,
          CHAT_PAGE_SIZE,
          oldest.created_at,
          oldest.id,
        );
        if (cancelled) return;
        const currentRef = chatState.selectedConversation;
        if (
          currentRef?.kind !== "workspace-topic" ||
          currentRef.workspaceId !== workspaceSearchJump.workspaceId ||
          currentRef.topicId !== workspaceSearchJump.topicId
        ) {
          return;
        }
        const olderRows = messages.map((m) =>
          serverWorkspaceMessageToRow(workspaceSearchJump.workspaceId, workspaceSearchJump.topicId, m),
        );
        if (olderRows.length === 0) {
          hasMore = false;
          break;
        }
        dispatchChat({
          type: "prepend-messages",
          messages: olderRows,
          hasMoreOlder: messages.length === CHAT_PAGE_SIZE,
          workspaceReactionsMerge: workspaceReactionsMapFromServerMessages(messages),
        });
        if (olderRows.some((row) => row.id === workspaceSearchJump.messageId)) {
          workspaceSearchJumpFetchKeyRef.current = null;
          return;
        }
        if (messages.length < CHAT_PAGE_SIZE) {
          hasMore = false;
          break;
        }
        const probe = await apiFetchWorkspaceTopicMessages(
          workspaceSearchJump.workspaceId,
          workspaceSearchJump.topicId,
          1,
          olderRows[0]!.created_at,
          olderRows[0]!.id,
        );
        if (cancelled) return;
        hasMore = probe.messages.length > 0;
        oldest = olderRows[0]!;
      }
      workspaceSearchJumpFetchKeyRef.current = null;
    })().catch((e: unknown) => {
      workspaceSearchJumpFetchKeyRef.current = null;
      console.warn("[styx] workspace search jump failed:", e);
    });

    return () => {
      cancelled = true;
    };
  }, [chatState.chatHasMoreOlder, chatState.messages, chatState.selectedConversation, dispatchChat, workspaceSearchJump]);

  const onVoiceCall = useCallback(() => {
    startVoiceCall(selectedDmPeerId);
  }, [startVoiceCall, selectedDmPeerId]);

  const { onChatInput: onChatInputBase } = useTypingPresence({
    conn,
    setChatInput: (value) => dispatchChat({ type: "set-chat-input", value }),
    sendRealtimeCommand,
  });

  const onChatInput = useCallback(
    (value: string) => {
      if (chatState.selectedConversation?.kind === "vault") touchActivity();
      onChatInputBase(value);
    },
    [chatState.selectedConversation, onChatInputBase, touchActivity],
  );

  const onForwardMessage = useCallback(
    (message: MessageRow) => {
      const sourceConversation = chatState.selectedConversation;
      if (!sourceConversation) return;
      setForwardDraft({
        message,
        sourceConversation,
        previewText: forwardPreviewFromPlaintext(message.plaintext),
      });
    },
    [chatState.selectedConversation],
  );

  const onConfirmForward = useCallback(
    async (targetConversation: ConversationRef): Promise<boolean> => {
      if (!forwardDraft) return false;
      if (targetConversation.kind === "vault") touchActivity();
      const ok = await forwardMessageToConversation(
        forwardDraft.message,
        forwardDraft.sourceConversation,
        targetConversation,
      );
      return ok;
    },
    [forwardDraft, forwardMessageToConversation, touchActivity],
  );

  const onSelectVaultRow = useCallback(
    (vaultId: string) => {
      setSettingsOpen(false);
      setMobileWorkspaceTopicsView(null);
      setSidebarWorkspace(null);
      dispatchChat({ type: "set-selected-conversation", conversation: { kind: "vault", vaultId } });
    },
    [dispatchChat],
  );

  const onVaultLockSidebar = useCallback(() => {
    if (chatState.selectedConversation?.kind === "vault") {
      dispatchChat({ type: "set-selected-conversation", conversation: null });
    }
  }, [chatState.selectedConversation, dispatchChat]);

  const vaultSidebarSlot = shellMain ? (
    <VaultSection
      selectedVaultId={selectedVaultId}
      onSelectVault={onSelectVaultRow}
      onVaultLock={onVaultLockSidebar}
      isMobile={narrow}
      embedded
      collapsible
      expanded={narrow ? mobileSidebarVaultsOpen : desktopSidebarVaultsOpen}
      onToggleExpanded={() =>
        narrow ? setMobileSidebarVaultsOpen((v) => !v) : setDesktopSidebarVaultsOpen((v) => !v)
      }
    />
  ) : null;

  const selectedContact = useMemo(
    () => (selectedDmPeerId ? contactsState.contacts.find((c) => c.user_id === selectedDmPeerId) : undefined),
    [contactsState.contacts, selectedDmPeerId],
  );

  useEffect(() => {
    if (!selectedDmPeerId || contactsState.contacts.length === 0) return;
    if (contactsState.contacts.some((c) => c.user_id === selectedDmPeerId)) return;
    dispatchChat({ type: "set-selected-conversation", conversation: null });
  }, [selectedDmPeerId, contactsState.contacts, dispatchChat]);

  const activePresence = selectedContact
    ? contactsState.presenceByUserId[selectedContact.user_id] ?? "offline"
    : "offline";

  const connectionLabel = useMemo(() => {
    const ru: Record<typeof conn, string> = {
      disconnected: "нет соединения",
      connecting: "подключение",
      open: "онлайн",
      closed: "обрыв",
    };
    return `${ru[conn]}${authenticated ? " // авторизован" : ""}`;
  }, [authenticated, conn]);

  const hasActiveChatPane =
    Boolean(selectedDmPeerId) ||
    chatState.selectedConversation?.kind === "workspace-topic" ||
    chatState.selectedConversation?.kind === "vault";
  const hasActiveWorkspaceTopicsPane = mobileWorkspaceTopicsView !== null;

  const showBoot = bootVisible && (!sodiumReady || !!identityRow || !!bootError);
  const showSidebar = !narrow || (!hasActiveChatPane && !hasActiveWorkspaceTopicsPane && !settingsOpen);
  const showChat = !narrow || hasActiveChatPane || hasActiveWorkspaceTopicsPane || settingsOpen;
  const showTopbar = !narrow || (!hasActiveChatPane && !hasActiveWorkspaceTopicsPane);

  useLayoutEffect(() => {
    if (!narrow || !showSidebar) return;
    const el = mobileSidebarScrollRootRef.current;
    if (el) el.scrollTop = savedMobileSidebarScrollTopRef.current;
  }, [narrow, showSidebar]);

  const reconnectOverlay = useReconnectOverlay(conn);
  const showConnOverlay = Boolean(identityRow && keys && !bootVisible && authenticated && reconnectOverlay);

  return (
    <div className="app-root">
      {visualMode === "terminal" && <MatrixRain />}
      {visualMode === "terminal" && <Scanlines />}

      {showBoot ? (
        <BootScreen
          items={bootItems}
          ready={Boolean(sodiumReady && (authenticated || (!identityRow && !keys) || !!bootError))}
          error={bootError}
          pwaUpdate={pwaBootUpdate}
          overlayMode={bootOverlayMode}
        />
      ) : null}

      {!identityRow || !keys || !getStoredJwt() ? (
        <main className="shell shell--boot">
          <div className="welcome-card">
            <div className="welcome-card__hero">
              <h1>{STYX_THEME.appName}</h1>
              <p className="tag">// local keys // Ed25519 + X25519 // trusted relay</p>
            </div>
            {identityRow && !getStoredJwt() ? (
              <form
                className="welcome-card__form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitLogin();
                }}
              >
                <p className="tag">Сессия истекла — войдите снова</p>
                <label className="lbl" htmlFor="login-email">
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                />
                <label className="lbl" htmlFor="login-pass">
                  Пароль
                </label>
                <input
                  id="login-pass"
                  type="password"
                  autoComplete="current-password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                />
                <button type="submit">Войти</button>
                {bootError ? <p className="err">{bootError}</p> : null}
              </form>
            ) : (
              <form
                className="welcome-card__form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (authTab === "register") {
                    void submitRegister();
                  } else {
                    void submitLogin();
                  }
                }}
              >
                <div className="auth-tabs" role="tablist">
                  <button
                    type="button"
                    className={authTab === "register" ? "auth-tabs__btn is-active" : "auth-tabs__btn"}
                    onClick={() => setAuthTab("register")}
                  >
                    Регистрация
                  </button>
                  <button
                    type="button"
                    className={authTab === "login" ? "auth-tabs__btn is-active" : "auth-tabs__btn"}
                    onClick={() => setAuthTab("login")}
                  >
                    Вход
                  </button>
                </div>
                {authTab === "register" ? (
                  <>
                    <label className="lbl" htmlFor="reg-invite">
                      Инвайт-код
                    </label>
                    <input
                      id="reg-invite"
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={regInviteCode}
                      onChange={(e) => setRegInviteCode(e.target.value)}
                      placeholder="код из списка админа"
                    />
                    <label className="lbl" htmlFor="reg-email">
                      Email
                    </label>
                    <input
                      id="reg-email"
                      type="email"
                      autoComplete="email"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                    />
                    <label className="lbl" htmlFor="reg-pass">
                      Пароль
                    </label>
                    <input
                      id="reg-pass"
                      type="password"
                      autoComplete="new-password"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                    />
                    <label className="lbl" htmlFor="reg-pass2">
                      Пароль ещё раз
                    </label>
                    <input
                      id="reg-pass2"
                      type="password"
                      autoComplete="new-password"
                      value={regPassword2}
                      onChange={(e) => setRegPassword2(e.target.value)}
                    />
                    <label className="lbl" htmlFor="reg-username">
                      Имя пользователя
                    </label>
                    <input
                      id="reg-username"
                      value={regUsername}
                      onChange={(e) => setRegUsername(e.target.value)}
                      placeholder="rogachev"
                      autoComplete="off"
                      maxLength={USERNAME_MAX_LEN}
                    />
                    <button type="submit">Создать аккаунт</button>
                  </>
                ) : (
                  <>
                    <label className="lbl" htmlFor="login-email2">
                      Email
                    </label>
                    <input
                      id="login-email2"
                      type="email"
                      autoComplete="username"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                    />
                    <label className="lbl" htmlFor="login-pass2">
                      Пароль
                    </label>
                    <input
                      id="login-pass2"
                      type="password"
                      autoComplete="current-password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                    />
                    <button type="submit">Войти</button>
                  </>
                )}
                {bootError ? <p className="err">{bootError}</p> : null}
              </form>
            )}
          </div>
        </main>
      ) : (
        <main className="shell shell--main">
          {showTopbar ? (
            <Header
              username={identityRow.username}
              connectionLabel={connectionLabel}
              isMobile={narrow}
              onSettings={() => setSettingsOpen(true)}
            />
          ) : null}

          {chatState.lastError ? (
            <div className="banner banner--error">
              <span>{chatState.lastError}</span>
              <button type="button" className="mini" onClick={() => dispatchChat({ type: "set-error", error: null })}>
                ×
              </button>
            </div>
          ) : null}

          {chatState.decryptError ? (
            <div className="banner banner--decrypt">
              <span>{chatState.decryptError}</span>
              <button
                type="button"
                className="mini"
                onClick={() => dispatchChat({ type: "set-decrypt-error", error: null })}
              >
                ×
              </button>
            </div>
          ) : null}

          {showPushBanner ? (
            <div className="banner banner--push">
              <div className="banner--push__text">
                <strong>Уведомления.</strong> На телефоне (Chrome) системный запрос разрешения работает только после
                нажатия кнопки — не при автоматическом входе. Пуш доставляется в фоне; если приложение открыто и в
                фокусе, уведомление не показывается (сообщение приходит по сети).
                {pushBannerError ? <span className="banner--push__err"> {pushBannerError}</span> : null}
              </div>
              <div className="banner--push__actions">
                <button type="button" className="mini mini--accent" onClick={onEnablePushTap}>
                  Включить
                </button>
                <button type="button" className="mini" onClick={onDismissPushBanner}>
                  Позже
                </button>
              </div>
            </div>
          ) : null}

          <ConnectionOverlay conn={conn} visible={showConnOverlay} />

          <div className={`layout ${narrow ? "layout--narrow" : ""}`}>
            {showSidebar ? (
              <aside className="sidebar">
                {narrow ? (
                  <div
                    ref={mobileSidebarScrollRootRef}
                    className="sidebar-inner sidebar-inner--scroll-root"
                    onScroll={(e) => {
                      if (!narrow || !showSidebar) return;
                      savedMobileSidebarScrollTopRef.current = e.currentTarget.scrollTop;
                    }}
                  >
                    <WorkspaceNavSection
                      enabled={shellMain}
                      selectedWorkspaceId={sidebarWorkspace?.id ?? null}
                      onSelectWorkspaceRow={onSelectWorkspaceRow}
                      collapsible
                      expanded={mobileSidebarWsOpen}
                      onToggleExpanded={() => setMobileSidebarWsOpen((v) => !v)}
                      listRefreshEpoch={workspaceNavListEpoch}
                    />
                    {vaultSidebarSlot}
                    <ContactList
                      contacts={contactsSortedByLastMessage}
                      incoming={contactsState.incoming}
                      selectedPeerId={selectedDmPeerId}
                      unreadCounts={contactsState.unreadCounts}
                      presenceByUserId={contactsState.presenceByUserId}
                      addUser={contactsState.addUser}
                      isMobile={narrow}
                      embedded
                      channelsCollapsible
                      channelsExpanded={mobileSidebarDmOpen}
                      onToggleChannels={() => setMobileSidebarDmOpen((v) => !v)}
                      onAddUserChange={(value) => dispatchContacts({ type: "set-add-user", value })}
                      onSendRequest={sendContactRequest}
                      onAcceptRequest={acceptRequest}
                      onSelectPeer={(peerId) => {
                        setSettingsOpen(false);
                        setMobileWorkspaceTopicsView(null);
                        setSidebarWorkspace(null);
                        dispatchChat({ type: "set-selected-conversation", conversation: { kind: "dm", peerId } });
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <WorkspaceNavSection
                      enabled={shellMain}
                      selectedWorkspaceId={sidebarWorkspace?.id ?? null}
                      onSelectWorkspaceRow={onSelectWorkspaceRow}
                      listRefreshEpoch={workspaceNavListEpoch}
                    />
                    <ContactList
                      sidebarBeforeFooter={vaultSidebarSlot}
                      contacts={contactsSortedByLastMessage}
                      incoming={contactsState.incoming}
                      selectedPeerId={selectedDmPeerId}
                      unreadCounts={contactsState.unreadCounts}
                      presenceByUserId={contactsState.presenceByUserId}
                      addUser={contactsState.addUser}
                      isMobile={narrow}
                      onAddUserChange={(value) => dispatchContacts({ type: "set-add-user", value })}
                      onSendRequest={sendContactRequest}
                      onAcceptRequest={acceptRequest}
                      onSelectPeer={(peerId) => {
                        setSettingsOpen(false);
                        setMobileWorkspaceTopicsView(null);
                        setSidebarWorkspace(null);
                        dispatchChat({ type: "set-selected-conversation", conversation: { kind: "dm", peerId } });
                      }}
                    />
                  </>
                )}
              </aside>
            ) : null}

            {!narrow && shellMain && sidebarWorkspace ? (
              <WorkspaceTopicRail
                enabled={shellMain}
                workspaceId={sidebarWorkspace.id}
                workspaceName={sidebarWorkspace.name}
                canCreateTopics={canCreateTopics}
                selectedTopicKey={selectedTopicKey}
                onSelectTopic={onSelectWorkspaceTopic}
                topicsRefreshEpoch={topicListEpoch[sidebarWorkspace.id] ?? 0}
                currentUserId={identityRow.userId}
                workspaceRole={sidebarWorkspace.role as "owner" | "admin" | "member"}
                onPinnedTopicIdsChange={onPinnedTopicIdsChange}
              />
            ) : null}

            {showChat ? (
              <section className="chat-pane">
                {settingsOpen ? (
                  <SettingsPanel
                    onClose={() => {
                      if (narrowChatBackAction(narrow) === "history") {
                        window.history.back();
                      } else {
                        setSettingsOpen(false);
                      }
                    }}
                    onLogout={() => void logoutLocal()}
                    username={identityRow.username}
                    currentAvatarUrl={myAvatarUrl}
                    onAvatarChanged={setMyAvatarUrls}
                    onRenameUsername={handleRenameUsername}
                    renameError={renameUsernameError}
                    onRenameBegin={() => setRenameUsernameError(null)}
                  />
                ) : mobileWorkspaceTopicsView ? (
                  <WorkspaceTopicsScreen
                    workspaceId={mobileWorkspaceTopicsView.id}
                    workspaceName={mobileWorkspaceTopicsView.name}
                    workspaceRole={mobileWorkspaceTopicsView.role}
                    canCreateTopics={canCreateTopics}
                    topicsRefreshEpoch={topicListEpoch[mobileWorkspaceTopicsView.id] ?? 0}
                    currentUserId={identityRow.userId}
                    resolveAuthorName={resolveTopicAuthorName}
                    onPinnedTopicIdsChange={onPinnedTopicIdsChange}
                    onSelectTopic={onSelectWorkspaceTopic}
                    onSelectSearchResult={onSelectWorkspaceSearchResult}
                    onWorkspaceDeleted={() => {
                      setMobileWorkspaceTopicsView(null);
                      setWorkspaceNavListEpoch((n) => n + 1);
                    }}
                    onWorkspaceRenamed={onWorkspaceRenamedFromTopics}
                    onBack={() => {
                      if (narrowChatBackAction(narrow) === "history") {
                        window.history.back();
                      } else {
                        setMobileWorkspaceTopicsView(null);
                      }
                    }}
                  />
                ) : (
                  <ChatView
                    conversationCapabilities={conversationCapabilities}
                    selectedContact={selectedContact}
                    workspaceTopicView={workspaceTopicForView}
                    vaultTopicView={vaultTopicForView}
                    fetchE2eEncryptedBlob={vaultTopicForView ? fetchVaultBlob : undefined}
                    presenceStatus={activePresence}
                    messages={chatState.messages}
                    chatHasMoreOlder={chatState.chatHasMoreOlder}
                    conversationLoading={conversationLoading}
                    loadingOlder={loadingOlder}
                    onLoadOlder={() => void loadOlderChatMessages()}
                    currentUserId={identityRow.userId}
                    currentUsername={identityRow.username}
                    currentUserAvatarUrl={myAvatarUrl}
                    currentUserAvatarUrls={myAvatarUrls}
                    chatInput={chatState.chatInput}
                    chatAttachmentDraft={chatState.chatAttachmentDraft}
                    chatReplyTo={chatState.chatReplyTo}
                    workspaceReactionsByMessageId={chatState.workspaceReactionsByMessageId}
                    sharedTopicPinned={
                      chatState.selectedConversation?.kind === "workspace-topic"
                        ? (pinnedTopicIdsByWorkspace[chatState.selectedConversation.workspaceId]?.includes(
                            chatState.selectedConversation.topicId,
                          ) ?? false)
                        : false
                    }
                    onToggleSharedTopicPin={() => void handleToggleSharedTopicPin()}
                    onReplyToMessage={(msg) =>
                      dispatchChat({
                        type: "set-chat-reply-to",
                        draft: {
                          messageId: msg.id,
                          from_id: msg.from_id,
                          previewText: previewTextForReply(msg.plaintext),
                        },
                      })
                    }
                    onForwardMessage={onForwardMessage}
                    onToggleReaction={(message, emoji, remove) => void toggleReaction(message, emoji, remove)}
                    onCancelReply={() => dispatchChat({ type: "set-chat-reply-to", draft: null })}
                    isMobile={narrow}
                    voiceCallEnabled={isWsLeader && authenticated}
                    onVoiceCall={onVoiceCall}
                    onBack={() => {
                      if (narrowChatBackAction(narrow) === "history") {
                        window.history.back();
                      } else {
                        if (narrow && chatState.selectedConversation?.kind === "workspace-topic") {
                          setMobileWorkspaceTopicsView({
                            id: chatState.selectedConversation.workspaceId,
                            name:
                              workspaceTopicLabels?.workspaceName ??
                              (sidebarWorkspace?.id === chatState.selectedConversation.workspaceId
                                ? sidebarWorkspace.name
                                : "// workspace"),
                            role:
                              sidebarWorkspace?.id === chatState.selectedConversation.workspaceId
                                ? sidebarWorkspace.role
                                : "member",
                          });
                        }
                        dispatchChat({ type: "set-selected-conversation", conversation: null });
                      }
                    }}
                    onChatInput={onChatInput}
                    onPickImage={(file) => void pickChatImage(file)}
                    onPickFile={(file) => void pickChatFile(file)}
                    onRemoveImage={removeChatImage}
                    onSendVoice={(plainBytes, mime, durationMs, waveform) =>
                      void sendVoiceMessage(plainBytes, mime, durationMs, waveform)
                    }
                    onSelectWorkspaceSearchResult={onSelectWorkspaceSearchResult}
                    requestedJumpMessageId={
                      workspaceSearchJump &&
                      chatState.selectedConversation?.kind === "workspace-topic" &&
                      chatState.selectedConversation.workspaceId === workspaceSearchJump.workspaceId &&
                      chatState.selectedConversation.topicId === workspaceSearchJump.topicId
                        ? workspaceSearchJump.messageId
                        : null
                    }
                    onJumpHandled={(messageId) =>
                      setWorkspaceSearchJump((current) =>
                        current?.messageId === messageId ? null : current,
                      )
                    }
                    onWorkspaceTopicDeleted={() => {
                      if (narrow && chatState.selectedConversation?.kind === "workspace-topic") {
                        setMobileWorkspaceTopicsView({
                          id: chatState.selectedConversation.workspaceId,
                          name:
                            workspaceTopicLabels?.workspaceName ??
                            (sidebarWorkspace?.id === chatState.selectedConversation.workspaceId
                              ? sidebarWorkspace.name
                              : "// workspace"),
                          role:
                            sidebarWorkspace?.id === chatState.selectedConversation.workspaceId
                              ? sidebarWorkspace.role
                              : "member",
                        });
                      }
                      dispatchChat({ type: "set-selected-conversation", conversation: null });
                      setWorkspaceNavListEpoch((n) => n + 1);
                    }}
                    onWorkspaceTopicRenamed={onWorkspaceTopicRenamed}
                    onSend={() => void sendChat()}
                  />
                )}
              </section>
            ) : null}
          </div>

          <VoiceCallOverlay
            ui={voiceUi}
            onAccept={() => voiceSessionRef.current?.acceptIncoming()}
            onReject={() => voiceSessionRef.current?.rejectIncoming()}
            onHangup={() => voiceSessionRef.current?.hangupLocal()}
            onDismissInfo={() => setVoiceUi({ phase: "idle" })}
            onToggleMute={() => voiceSessionRef.current?.toggleMicMute()}
            remoteAudioRef={remoteAudioRef}
          />
          {forwardDraft ? (
            <ForwardMessageModal
              previewText={forwardDraft.previewText}
              contacts={contactsSortedByLastMessage}
              onClose={() => setForwardDraft(null)}
              onConfirm={onConfirmForward}
            />
          ) : null}
        </main>
      )}
    </div>
  );
}

export function StyxAppShell() {
  return (
    <StyxAppProviders>
      <StyxShellMain />
    </StyxAppProviders>
  );
}
